import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Lock,
  LogOut,
  Plus,
  Trash2,
  ExternalLink,
  FileText,
  Phone,
  FolderOpen,
  ShieldAlert,
  Loader2,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "rb_admin_token";

const absLink = (link) => (link?.startsWith("http") ? link : `${BACKEND}${link}`);

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const authed = !!token;

  const logout = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
  }, []);

  if (!authed) return <Login onLogin={setToken} />;
  return <Dashboard token={token} onLogout={logout} />;
}

function Login({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem(TOKEN_KEY, r.data.token);
      onLogin(r.data.token);
    } catch {
      toast.error("Password errata");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f9f8f5] px-5">
      <div className="w-full max-w-sm rounded-2xl border border-slate-100 bg-white p-8 shadow-sm">
        <div className="mb-6 flex justify-center">
          <Logo />
        </div>
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-[#1a1a1a]/5">
            <Lock className="h-5 w-5 text-[#1a1a1a]" />
          </div>
          <h1 className="font-serif text-2xl font-semibold text-[#1a1a1a]">Area Staff</h1>
          <p className="mt-1 text-sm text-slate-500">Accesso riservato allo studio</p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="pwd" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Password
            </Label>
            <Input
              id="pwd"
              type="password"
              data-testid="admin-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="bg-white"
            />
          </div>
          <Button
            type="submit"
            data-testid="admin-login-btn"
            disabled={loading || !password}
            className="h-12 w-full rounded-full bg-[#1a1a1a] font-semibold text-white transition-colors hover:bg-[#C9A84C] hover:text-[#1a1a1a]"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Accedi"}
          </Button>
        </form>
        <a href="/" className="mt-4 block text-center text-xs text-slate-400 hover:text-[#1a1a1a]">
          ← Torna al portale
        </a>
      </div>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const headers = useMemo(() => ({ "X-Admin-Token": token }), [token]);

  return (
    <div className="min-h-screen bg-[#f9f8f5]">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-[#f9f8f5]/95 backdrop-blur">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-3">
          <Logo />
          <Button
            variant="ghost"
            data-testid="admin-logout-btn"
            onClick={onLogout}
            className="text-slate-500 hover:text-[#1a1a1a]"
          >
            <LogOut className="mr-1.5 h-4 w-4" /> Esci
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 py-8">
        <h1 className="mb-6 font-serif text-3xl font-semibold text-[#1a1a1a]">Pannello Staff</h1>
        <Tabs defaultValue="submissions">
          <TabsList className="mb-6 bg-white">
            <TabsTrigger value="submissions" data-testid="tab-submissions">Invii ricevuti</TabsTrigger>
            <TabsTrigger value="types" data-testid="tab-types">Tipi documento</TabsTrigger>
          </TabsList>
          <TabsContent value="submissions">
            <Submissions headers={headers} onExpire={onLogout} />
          </TabsContent>
          <TabsContent value="types">
            <DocTypes headers={headers} onExpire={onLogout} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function Submissions({ headers, onExpire }) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/submissions`, { headers });
      setData(r.data);
    } catch (e) {
      if (e?.response?.status === 401) onExpire();
    }
  }, [headers, onExpire]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <Loader />;
  const { submissions, drive_enabled } = data;

  return (
    <div className="space-y-4">
      {!drive_enabled && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="drive-mock-warning">
          <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div className="text-sm text-amber-800">
            <strong>Google Drive non collegato (modalità locale).</strong> I file sono salvati sul
            server dell'app. Fornisci le credenziali del Service Account per attivare il salvataggio
            reale su Drive.
          </div>
        </div>
      )}
      <div className="text-sm text-slate-500">{submissions.length} invii totali</div>
      {submissions.length === 0 ? (
        <div className="rounded-xl border border-slate-100 bg-white p-10 text-center text-slate-400 shadow-sm">
          Nessun invio ancora ricevuto.
        </div>
      ) : (
        <div className="space-y-3">
          {submissions.map((s) => (
            <div key={s.id} className="rounded-xl border border-slate-100 bg-white p-5 shadow-sm" data-testid="submission-row">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-serif text-lg font-semibold text-[#1a1a1a]">{s.client_name}</div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                    <span>{s.created_at_local}</span>
                    <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {s.telefono}</span>
                    <span className="rounded bg-slate-100 px-1.5 py-0.5 uppercase tracking-wide">
                      {s.storage_mode === "drive" ? "Drive" : "Locale"}
                    </span>
                  </div>
                </div>
                <a
                  href={absLink(s.folder_link)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-sm font-medium text-[#1a1a1a] hover:text-[#C9A84C]"
                >
                  <FolderOpen className="h-4 w-4" /> Cartella <ExternalLink className="h-3 w-3" />
                </a>
              </div>
              <div className="mt-3 grid gap-2 border-t border-slate-100 pt-3 sm:grid-cols-2">
                {s.files.map((f, i) => (
                  <a
                    key={`${s.id}-${f.stored_name}-${i}`}
                    href={absLink(f.link)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-100"
                  >
                    <FileText className="h-4 w-4 shrink-0 text-[#C9A84C]" />
                    <span className="truncate">{f.stored_name}</span>
                    <span className="ml-auto shrink-0 text-xs text-slate-400">
                      {(f.size / 1024 / 1024).toFixed(1)}MB
                    </span>
                  </a>
                ))}
              </div>
              <div className="mt-2 text-[11px] text-slate-400">
                Consenso privacy registrato · versione {s.privacy_version}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DocTypes({ headers, onExpire }) {
  const [types, setTypes] = useState(null);
  const [label, setLabel] = useState("");
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/document-types`, { headers });
      setTypes(r.data);
    } catch (e) {
      if (e?.response?.status === 401) onExpire();
    }
  }, [headers, onExpire]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!label.trim()) return;
    setAdding(true);
    try {
      await axios.post(`${API}/admin/document-types`, { label: label.trim().toUpperCase(), order: (types?.length || 0) }, { headers });
      setLabel("");
      await load();
      toast.success("Tipo documento aggiunto");
    } catch {
      toast.error("Errore");
    } finally {
      setAdding(false);
    }
  };

  const toggle = async (t) => {
    await axios.put(`${API}/admin/document-types/${t.id}`, { active: !t.active }, { headers });
    load();
  };

  const remove = async (t) => {
    await axios.delete(`${API}/admin/document-types/${t.id}`, { headers });
    load();
    toast.success("Tipo documento rimosso");
  };

  if (!types) return <Loader />;

  return (
    <div className="max-w-2xl space-y-4">
      <div className="flex gap-2">
        <Input
          data-testid="new-doctype-input"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="Nuovo tipo di documento (es. VISTO STUDIO)"
          className="bg-white"
        />
        <Button
          data-testid="add-doctype-btn"
          onClick={add}
          disabled={adding || !label.trim()}
          className="rounded-full bg-[#1a1a1a] text-white transition-colors hover:bg-[#C9A84C] hover:text-[#1a1a1a]"
        >
          <Plus className="mr-1 h-4 w-4" /> Aggiungi
        </Button>
      </div>

      <div className="divide-y divide-slate-100 rounded-xl border border-slate-100 bg-white shadow-sm">
        {types.map((t) => (
          <div key={t.id} className="flex items-center justify-between px-5 py-3" data-testid="doctype-row">
            <span className={`text-sm font-medium ${t.active ? "text-[#1a1a1a]" : "text-slate-400 line-through"}`}>
              {t.label}
            </span>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch checked={t.active} onCheckedChange={() => toggle(t)} data-testid={`toggle-${t.id}`} />
                <span className="text-xs text-slate-400">{t.active ? "Attivo" : "Nascosto"}</span>
              </div>
              <button
                onClick={() => remove(t)}
                aria-label="Rimuovi"
                data-testid={`delete-${t.id}`}
                className="text-slate-400 transition-colors hover:text-red-500"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const Loader = () => (
  <div className="flex justify-center py-16">
    <Loader2 className="h-6 w-6 animate-spin text-[#1a1a1a]" />
  </div>
);
